/**
 * Contextual search queries — the reference implementation of
 * docs/search/query-grammar.md (query.v1). Sales and Pocket implement the same
 * rules and run the same golden file (docs/search/query-golden.json); keep this
 * a literal transcription of the grammar so the three cannot drift.
 *
 * Deterministic, no AI: recognised phrases become filters (shown as chips) and
 * every other word stays in `q`, the normal search.v1 text query.
 */
import { normalize, osa } from './scoring';

export type QueryEntity =
  | 'unit' | 'group' | 'site' | 'company' | 'work_order' | 'checkin'
  | 'contact' | 'quote' | 'rental';

export type DateField = 'active' | 'created' | 'activity' | 'occurred';

export interface QueryDate {
  /** null = each entity's default field (no entity chip). */
  field: DateField | null;
  /** Local calendar dates, half-open: from ≤ day < to. */
  from: string;
  to: string;
  /** True when no date was typed and the grammar supplied one (R6). */
  implicit?: true;
}

export interface QueryFilters {
  entity?: QueryEntity;
  company_ids?: string[];
  date?: QueryDate;
  model_codes?: string[];
  status?: string[];
}

export interface ParsedQuery {
  q: string;
  filters: QueryFilters;
}

export interface ParseContext {
  now: Date;
  /** IANA zone the relative dates resolve in (the user's). */
  timeZone: string;
  /** Only companies the caller may see (R4): a parse never names an invisible company. */
  companies: readonly { id: string; name: string; callsigns?: readonly string[] }[];
  models: readonly { code: string; rooms?: number; callsigns?: readonly string[] }[];
}

// ------------------------------------------------------------------ vocabulary

/** Longest phrases first so "work orders" wins over "orders". */
const ENTITY_NOUNS: readonly (readonly [string, QueryEntity])[] = [
  ['work orders', 'work_order'], ['work order', 'work_order'], ['check ins', 'checkin'], ['check in', 'checkin'],
  ['wos', 'work_order'], ['checkins', 'checkin'], ['checkin', 'checkin'],
  ['productions', 'group'], ['production', 'group'], ['shows', 'group'], ['show', 'group'],
  ['groups', 'group'], ['group', 'group'], ['sites', 'site'], ['site', 'site'],
  ['units', 'unit'], ['unit', 'unit'], ['trailers', 'unit'], ['trailer', 'unit'], ['trucks', 'unit'], ['truck', 'unit'],
  ['companies', 'company'], ['company', 'company'], ['studios', 'company'], ['studio', 'company'],
  ['contacts', 'contact'], ['contact', 'contact'], ['people', 'contact'],
  ['quotes', 'quote'], ['quote', 'quote'], ['rentals', 'rental'], ['rental', 'rental'],
];
const ENTITY_WORDS = new Set(ENTITY_NOUNS.flatMap(([p]) => p.split(' ')));

/** R5: what an entity's date range means; absent = no date filter for it in v1. */
const DEFAULT_DATE_FIELD: Partial<Record<QueryEntity, DateField>> = {
  group: 'active', site: 'active', rental: 'active',
  contact: 'created', quote: 'created', work_order: 'created',
  unit: 'activity', checkin: 'occurred',
};

const FILLER = new Set(['all', 'list', 'show', 'me', 'find', 'with', 'from', 'in', 'during',
  'that', 'which', 'the', 'of', 'for']);
const STATUS_WORDS = new Set(['active', 'ended', 'available', 'rented']);
const DATE_KEYWORDS = new Set(['today', 'yesterday', 'this', 'last', 'week', 'month', 'year', 'days',
  'day', 'between', 'since', 'room', 'rooms']);
const NUMBER_WORDS: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5 };
const MONTH_NAMES = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
  'september', 'october', 'november', 'december'];

/** 0-11 for a month name or its prefix of ≥ 3 letters ("sep", "sept"), else -1. */
function monthIndex(w: string): number {
  return w.length >= 3 ? MONTH_NAMES.findIndex((m) => m.startsWith(w)) : -1;
}

function isYear(w: string): boolean {
  return /^(19|20)[0-9]{2}$/.test(w);
}

// ------------------------------------------------------------------ local dates

interface Ymd { y: number; m: number; d: number } // m: 0-11

function localToday(now: Date, timeZone: string): Ymd {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(now);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return { y: get('year'), m: get('month') - 1, d: get('day') };
}

function addDays(a: Ymd, n: number): Ymd {
  const t = new Date(Date.UTC(a.y, a.m, a.d + n));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth(), d: t.getUTCDate() };
}

function monthStart(y: number, m: number): Ymd {
  const t = new Date(Date.UTC(y, m, 1));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth(), d: 1 };
}

function iso(a: Ymd): string {
  return `${a.y}-${String(a.m + 1).padStart(2, '0')}-${String(a.d).padStart(2, '0')}`;
}

/** Monday of the ISO week containing `a`. */
function weekStart(a: Ymd): Ymd {
  const dow = new Date(Date.UTC(a.y, a.m, a.d)).getUTCDay(); // 0 = Sunday
  return addDays(a, -((dow + 6) % 7));
}

/** The most recent year in which month `m` has started (this year, else last). */
function recentYear(m: number, today: Ymd): number {
  return m <= today.m ? today.y : today.y - 1;
}

// ------------------------------------------------------------------ the parser

interface Word { w: string; raw: number; used: boolean }
interface Range { from: Ymd; to: Ymd; at: number[] }

/** A date phrase starting at word i, or null. `nx(k)` is the k-th free word after i. */
function matchDate(i: number, w: string, nx: (k: number) => string, today: Ymd): Omit<Range, 'at'> & { n: number } | null {
  const r = (from: Ymd, to: Ymd, n: number) => ({ from, to, n });
  if (w === 'today') return r(today, addDays(today, 1), 1);
  if (w === 'yesterday') return r(addDays(today, -1), today, 1);
  if ((w === 'this' || w === 'last') && ['week', 'month', 'year'].includes(nx(1))) {
    const back = w === 'last' ? 1 : 0;
    if (nx(1) === 'week') {
      const ws = addDays(weekStart(today), -7 * back);
      return r(ws, addDays(ws, 7), 2);
    }
    if (nx(1) === 'month') {
      const m0 = monthStart(today.y, today.m - back);
      return r(m0, monthStart(m0.y, m0.m + 1), 2);
    }
    const y = today.y - back;
    return r({ y, m: 0, d: 1 }, { y: y + 1, m: 0, d: 1 }, 2);
  }
  if (w === 'last' && /^[0-9]+$/.test(nx(1)) && ['days', 'day'].includes(nx(2))) {
    const n = Number(nx(1));
    return n >= 1 && n <= 366 ? r(addDays(today, -n + 1), addDays(today, 1), 3) : null;
  }
  if (w === 'between' && monthIndex(nx(1)) >= 0 && nx(2) === 'and' && monthIndex(nx(3)) >= 0) {
    const a = monthIndex(nx(1));
    const b = monthIndex(nx(3));
    const wrap = b < a ? 1 : 0;
    const y0 = isYear(nx(4)) ? Number(nx(4)) - wrap : recentYear(a, today);
    return r(monthStart(y0, a), monthStart(y0 + wrap, b + 1), isYear(nx(4)) ? 5 : 4);
  }
  if (/^q[1-4]$/.test(w)) {
    const q0 = (Number(w[1]) - 1) * 3;
    const y = isYear(nx(1)) ? Number(nx(1)) : recentYear(q0, today);
    return r(monthStart(y, q0), monthStart(y, q0 + 3), isYear(nx(1)) ? 2 : 1);
  }
  if (w === 'since' && isYear(nx(1))) return r({ y: Number(nx(1)), m: 0, d: 1 }, addDays(today, 1), 2);
  if (w === 'since' && monthIndex(nx(1)) >= 0) {
    const m = monthIndex(nx(1));
    const y = isYear(nx(2)) ? Number(nx(2)) : recentYear(m, today);
    return r(monthStart(y, m), addDays(today, 1), isYear(nx(2)) ? 3 : 2);
  }
  if (monthIndex(w) >= 0 && isYear(nx(1))) {
    const m = monthIndex(w);
    return r(monthStart(Number(nx(1)), m), monthStart(Number(nx(1)), m + 1), 2);
  }
  if (['in', 'during', 'from'].includes(w) && monthIndex(nx(1)) >= 0 && !isYear(nx(2))) {
    const m = monthIndex(nx(1));
    const y = recentYear(m, today);
    return r(monthStart(y, m), monthStart(y, m + 1), 2);
  }
  if (['in', 'during', 'from'].includes(w) && isYear(nx(1))) {
    const y = Number(nx(1));
    return r({ y, m: 0, d: 1 }, { y: y + 1, m: 0, d: 1 }, 2);
  }
  return null;
}

export function parseQuery(input: string, ctx: ParseContext): ParsedQuery {
  const rawTokens = input.trim().split(/\s+/).filter(Boolean);
  const words: Word[] = [];
  rawTokens.forEach((raw, i) => {
    const n = normalize(raw);
    if (n) for (const w of n.split(' ')) words.push({ w, raw: i, used: false });
  });
  const today = localToday(ctx.now, ctx.timeZone);
  const filters: QueryFilters = {};

  const free = (i: number) => i >= 0 && i < words.length && !words[i].used;
  const take = (i: number, n: number) => { for (let k = i; k < i + n; k += 1) words[k].used = true; };
  const spanFree = (i: number, n: number) => i + n <= words.length && words.slice(i, i + n).every((x) => !x.used);
  const span = (i: number, n: number) => words.slice(i, i + n).map((x) => x.w).join(' ');

  // 1. Companies: longest span first; a unique best tier wins (R4). A span with
  //    an entity noun counts only as an exact name or callsign ("Universal Studios").
  const companyIds: string[] = [];
  const companySpans: [number, number][] = [];
  for (let n = Math.min(4, words.length); n >= 1; n -= 1) {
    for (let i = 0; i + n <= words.length; i += 1) {
      if (!spanFree(i, n)) continue;
      const phrase = span(i, n);
      if (phrase.replace(/ /g, '').length < 3) continue;
      if (n === 1 && (FILLER.has(phrase) || STATUS_WORDS.has(phrase) || DATE_KEYWORDS.has(phrase)
        || monthIndex(phrase) >= 0 || isYear(phrase))) continue;
      const exactOnly = phrase.split(' ').some((w) => ENTITY_WORDS.has(w));
      let best = 9;
      let hits: string[] = [];
      for (const c of ctx.companies) {
        const name = normalize(c.name);
        let tier = 9;
        if (name === phrase || (c.callsigns ?? []).some((s) => normalize(s) === phrase)) tier = 1;
        else if (!exactOnly && name.startsWith(`${phrase} `)) tier = 2;
        else if (!exactOnly && ` ${name} `.includes(` ${phrase} `)) tier = 3;
        if (tier < best) { best = tier; hits = [c.id]; } else if (tier === best && tier < 9) hits.push(c.id);
      }
      if (best < 9 && hits.length === 1 && !companyIds.includes(hits[0])) {
        companyIds.push(hits[0]);
        companySpans.push([i, n]);
        take(i, n);
      }
    }
  }
  if (companyIds.length) filters.company_ids = companyIds;

  // 2. The first recognisable date phrase (later ones stay text).
  let range: Range | null = null;
  for (let i = 0; i < words.length && !range; i += 1) {
    if (!free(i)) continue;
    const m = matchDate(i, words[i].w, (k) => (free(i + k) ? words[i + k].w : ''), today);
    if (m) {
      range = { from: m.from, to: m.to, at: Array.from({ length: m.n }, (_, k) => i + k) };
      take(i, m.n);
    }
  }

  // 3. Rooms → model codes ("1 room", "one rooms", "1-room", "1room").
  const modelCodes: string[] = [];
  const addModels = (codes: string[]) => { for (const c of codes) if (!modelCodes.includes(c)) modelCodes.push(c); };
  for (let i = 0; i < words.length; i += 1) {
    if (!free(i)) continue;
    // N is 1–5 only (grammar §3.3): "6 rooms" stays text.
    const glued = /^([1-5]|one|two|three|four|five)rooms?$/.exec(words[i].w);
    const n = glued ? glued[1] : words[i].w;
    const count = /^[1-5]$/.test(n) ? Number(n) : NUMBER_WORDS[n];
    const roomNext = !glued && free(i + 1) && /^(room|rooms|rm|rms)$/.test(words[i + 1].w);
    if (count && (glued || roomNext)) {
      const codes = ctx.models.filter((m) => m.rooms === count).map((m) => m.code);
      if (codes.length) { addModels(codes); take(i, glued ? 1 : 2); }
    }
  }

  // 4. The first entity noun in query order ("show …" at the start is a command).
  for (let i = 0; i < words.length && !filters.entity; i += 1) {
    for (const [phrase, entity] of ENTITY_NOUNS) {
      const n = phrase.split(' ').length;
      const command = phrase === 'show' && i === 0 && words.length > 1;
      if (!command && spanFree(i, n) && span(i, n) === phrase) { filters.entity = entity; take(i, n); break; }
    }
  }
  // One typo on a long entity noun ("prodcutions").
  for (let i = 0; i < words.length && !filters.entity; i += 1) {
    if (!free(i) || words[i].w.length < 7) continue;
    const hit = ENTITY_NOUNS.find(([p]) => !p.includes(' ') && p.length >= 7 && osa(words[i].w, p) === 1);
    if (hit) { filters.entity = hit[1]; take(i, 1); }
  }

  // 5. Model codes / callsigns and 6. status words: only alongside an entity or
  //    a rooms filter, so "honeywagon" alone stays a plain text search.
  if (filters.entity || modelCodes.length) {
    for (let i = 0; i < words.length; i += 1) {
      if (!free(i)) continue;
      const w = words[i].w;
      const m = ctx.models.find((x) => normalize(x.code).replace(/ /g, '') === w)
        ?? ctx.models.find((x) => (x.callsigns ?? []).some((s) => normalize(s) === w));
      if (m) { addModels([m.code]); take(i, 1); }
    }
  }
  if (modelCodes.length) filters.model_codes = modelCodes;
  if (filters.entity) {
    const status: string[] = [];
    words.forEach((x) => { if (!x.used && STATUS_WORDS.has(x.w)) { status.push(x.w); x.used = true; } });
    if (status.length) filters.status = status;
    if (!range) {
      // A bare year with an entity is a date ("productions 2025").
      const yi = words.findIndex((x) => !x.used && isYear(x.w));
      if (yi >= 0) {
        const y = Number(words[yi].w);
        range = { from: { y, m: 0, d: 1 }, to: { y: y + 1, m: 0, d: 1 }, at: [yi] };
        words[yi].used = true;
      }
    }
  }

  // A date needs a field: an entity without one (companies) leaves the phrase as text.
  const field = filters.entity ? DEFAULT_DATE_FIELD[filters.entity] ?? null : null;
  const r = range as Range | null;
  if (r && filters.entity && !field) {
    for (const k of r.at) words[k].used = false;
  } else if (r) {
    filters.date = { field, from: iso(r.from), to: iso(r.to) };
  }
  // R6: productions filtered by model with no date mean "active today".
  if (filters.entity === 'group' && modelCodes.length && !filters.date) {
    filters.date = { field: 'active', from: iso(today), to: iso(addDays(today, 1)), implicit: true };
  }

  // A company on its own stays text: "disney" should find Disney, not list
  // everything Disney owns. A company chip needs another slot beside it.
  if (filters.company_ids && Object.keys(filters).length === 1) {
    delete filters.company_ids;
    for (const [i, n] of companySpans) for (let k = i; k < i + n; k += 1) words[k].used = false;
  }

  // 7. Filler is consumed only when something was understood; otherwise the
  //    query is left exactly as typed ("the office" stays a text search).
  const understood = Object.keys(filters).length > 0;
  if (!understood) return { q: rawTokens.join(' '), filters };
  words.forEach((x) => { if (!x.used && FILLER.has(x.w)) x.used = true; });
  const q = rawTokens.filter((_, i) => words.some((x) => x.raw === i && !x.used)).join(' ');
  return { q, filters };
}
